import {
  Button,
  ButtonList,
  DropdownButton,
  Page,
  SearchInput,
} from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import { useCallback, useState } from 'react';
import { useEffectOnce } from 'react-use';

import HoldMusicModal from 'components/powerCallFlow/components/modals/holdMusic/containers/holdMusicModal';
import {
  withPhoneNumbers,
  WithPhoneNumbersInjectedProps,
} from 'hocs/telephony';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useCurrentUser } from 'utils/user';

import { ExternalCallerIdModal } from '../../../containers/sections/telephony/externalCallerIdModal';

import BuyNumber from './modals/buyNumber';
import { NumberTable } from './numberTable';

export type Props = WithPhoneNumbersInjectedProps;

enum ModalType {
  BuyNumberModal,
  ExternalCallerModal,
  HoldMusicModal,
}

export const PhoneImpl = ({ phoneNumbers }: Props): JSX.Element => {
  const {
    currentRole: { atLeastAdmin },
  } = useCurrentUser();

  const { getItems, params } = phoneNumbers;

  const dispatch = useAppDispatch();
  const [openModalType, setOpenModalType] = useState<ModalType | null>(null);
  const handleHideModal = useCallback(() => setOpenModalType(null), []);

  const handleSearchToggle = (isOpen: boolean) => {
    if (!isOpen && params.search) {
      phoneNumbers.paramsChange({ search: null }, 0);
    }
  };

  const handleSearch = (search: string) => {
    ampli.phoneSearchApplied();

    if (search === params.search) return;

    phoneNumbers.paramsChange({ search }, 0);
  };

  useEffectOnce(() => {
    getItems();
  });

  return (
    <>
      <Page.Section
        title="All phone numbers"
        controls={
          <>
            <SearchInput
              onToggle={handleSearchToggle}
              onSubmit={handleSearch}
            />

            <DropdownButton
              onToggle={(isOpen) => isOpen && ampli.phoneActionsMenuOpened()}
              dropdown={
                <ButtonList>
                  <ButtonList.Item
                    onClick={() => {
                      ampli.phoneActionsMenuActionAddOutboundCallerIdClicked();
                      setOpenModalType(ModalType.ExternalCallerModal);
                    }}
                  >
                    Add external caller ID
                  </ButtonList.Item>
                  <ButtonList.Item
                    onClick={() => {
                      ampli.phoneActionsMenuActionChangeHoldMusicClicked();
                      setOpenModalType(ModalType.HoldMusicModal);
                    }}
                  >
                    Change hold music
                  </ButtonList.Item>
                </ButtonList>
              }
            >
              Actions
            </DropdownButton>

            {atLeastAdmin && (
              <Button
                variant="primary"
                id="buy-number-btn"
                onClick={() => {
                  ampli.phoneFormOpened();
                  dispatch({ type: 'TELEPHONY_BUY_NUMBER_CLICKED' });
                  setOpenModalType(ModalType.BuyNumberModal);
                }}
              >
                Buy number
              </Button>
            )}
          </>
        }
      >
        <NumberTable phoneNumbers={phoneNumbers} />
      </Page.Section>

      {openModalType === ModalType.BuyNumberModal && (
        <BuyNumber
          isOpen
          hideSearchNumberModal={handleHideModal}
          fetchNumbers={getItems}
        />
      )}
      {openModalType === ModalType.ExternalCallerModal && (
        <ExternalCallerIdModal isOpen onClose={handleHideModal} />
      )}
      {openModalType === ModalType.HoldMusicModal && (
        <HoldMusicModal isOpen onClose={handleHideModal} />
      )}
    </>
  );
};

export const Phone = withPhoneNumbers(PhoneImpl, {
  initialParams: { limit: '12' },
});
